import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { LoginCallback } from "@okta/okta-react";
import React from "react";
import { createRoot } from "react-dom/client";
import { pdfjs } from "react-pdf";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router";
import { App } from "./App";
import InternalError from "./components/Errors/InternalError";
import { NotFoundPage } from "./components/Errors/NotFound";
import OrionSecurePage from "./components/OktaAuth/OrionSecurePage";
import SecurePage from "./components/OktaAuth/SecurePage";
import SiriusSecurePage from "./components/OktaAuth/SiriusSecurePage";
import "./index.css";
import AgentsPage from "./pages/AgentsPage";
import { AssetsListingPage } from "./pages/AssetListing";
import { AssetPage } from "./pages/AssetPage";
import ChatPage from "./pages/ChatPage";
import { ComingSoonPage } from "./pages/ComingSoonPage";
import { ConfigListing } from "./pages/ConfigListing";
import { ConfigPage } from "./pages/ConfigPage";
import { Home } from "./pages/Home";
import NewAgentPage from "./pages/NewAgentPage";
import NewAssetPage from "./pages/NewAssetPage";
import NewChatPage from "./pages/NewChatPage";
import { ProcessingPage } from "./pages/ProcessingPage";
import ProfilePage from "./pages/ProfilePage";
import { RecentChatsPage } from "./pages/RecentChats";
import { WorkspacePage } from "./pages/WorkspacePage";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const rootElement = document.getElementById("root");
const root = createRoot(rootElement as HTMLElement);

// All `Portal`-related components need to have the the main app wrapper element as a container
// so that the are in the subtree under the element used in the `important` option of the Tailwind's config.
export const theme = createTheme({
  cssVariables: true,
  palette: {
    mode: "light",
    primary: {
      main: "#1c1790",
    },
    secondary: {
      main: "#ab8de9",
    },
    info: {
      main: "#0049fb",
    },
  },
  components: {
    MuiPopover: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiPopper: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            boxShadow: "0 0 0px 1000px #e7f0fe inset",
            WebkitTextFillColor: "#000", // Couleur du texte
            transition: "background-color 5000s ease-in-out 0s",
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeader: {
          backgroundColor: "var(--mui-palette-primary-main)",
          color: "white",
        },
        cell: {
          ":focus": {
            outline: "none",
          },
        },
        menuIconButton: {
          "& .MuiSvgIcon-root": {
            color: "white",
          },
        },
        columnHeaderCheckbox: {
          "& .MuiCheckbox-root": {
            color: "white",
          },
        },
        sortIcon: {
          color: "white",
        },

        columnSeparator: {
          display: "none",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          "& .MuiIconButton-root": { color: "white" },
          backgroundColor: "var(--mui-palette-primary-main)",
          color: "white",
        },
      },
    },
    MuiList: {
      defaultProps: {
        dense: true,
      },
    },
    MuiMenuItem: {
      defaultProps: {
        dense: true,
      },
    },
    MuiTable: {
      defaultProps: {
        size: "small",
      },
    },
  },
});

const router = createBrowserRouter([
  {
    path: "/",
    Component: App,
    errorElement: <NotFoundPage />,
    children: [
      {
        path: "login/callback",
        Component: LoginCallback,
      },
      {
        path: "",
        Component: SecurePage,
        errorElement: <InternalError />,
        children: [
          {
            path: "",
            Component: Home,
          },
          {
            path: "user/:userId",
            Component: ProfilePage,
          },
          {
            path: "workspaces",
            Component: WorkspacePage,
          },
          {
            path: "orion",
            Component: OrionSecurePage,
            children: [
              {
                path: "assets",
                Component: AssetsListingPage,
              },
              {
                path: "configs",
                Component: ConfigListing,
              },
              {
                path: "config/:configId",
                Component: ConfigPage,
              },
              {
                path: "asset/new",
                Component: NewAssetPage,
              },
              {
                path: "asset/:assetId",
                Component: AssetPage,
              },
              {
                path: "processing/:processingId/:coreIdx",
                Component: ProcessingPage,
              },
              {
                path: "api",
                Component: ComingSoonPage,
              },
            ],
          },
          {
            path: "sirius",
            Component: SiriusSecurePage,
            children: [
              {
                path: "bots",
                Component: AgentsPage,
              },
              {
                path: "bot/new",
                Component: NewAgentPage,
              },
              {
                path: "bot/:botId",
                Component: Outlet,
                children: [
                  {
                    path: "",
                    Component: RecentChatsPage,
                  },
                  {
                    path: "chat/new",
                    Component: NewChatPage,
                  },
                ],
              },
              {
                path: "chat/:chatId",
                Component: ChatPage,
              },
              {
                path: "connectors",
                Component: ComingSoonPage,
              },
            ],
          },
        ],
      },
    ],
  },
]);

root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);
